import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { publicApi, errMsg } from "@/lib/api";
import { useMe, usePageTitle } from "@/lib/site";

const MIN_PW = 6;

const AuthShell = ({ title, subtitle, children, testId }) => (
  <div className="container-x py-12 md:py-16 grid lg:grid-cols-2 gap-10 items-center min-h-[70vh]">
    <div className="hidden lg:flex flex-col gap-5">
      <img src="/logo-transparent.png" alt="هِمّة تم" className="w-80" />
      <h2 className="font-display text-4xl font-extrabold">مرحباً بك في <span className="gold-text">أكاديمية هِمّة تم</span></h2>
      <p className="text-[var(--muted-ink)] leading-loose">مع هِمّة تم تعلّم مهارات المستقبل وحوّل أفكارك إلى مشاريع حقيقية</p>
    </div>
    <div className="flex items-center justify-center">
      <div className="card-lux !transform-none w-full max-w-md p-7 md:p-9" data-testid={testId}>
        <h1 className="font-display text-2xl font-extrabold">{title}</h1>
        {subtitle && <p className="text-sm text-[var(--muted-ink)] mt-1 mb-6">{subtitle}</p>}
        {children}
      </div>
    </div>
  </div>
);

const Field = ({ id, label, ...p }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} className="h-12 bg-white" data-testid={`${id}-input`} {...p} />
  </div>
);

const PasswordField = ({ id, label, value, onChange, autoComplete = "new-password" }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input id={id} type={show ? "text" : "password"} dir="ltr" required minLength={MIN_PW} value={value} onChange={onChange}
          autoComplete={autoComplete} className="h-12 bg-white pl-11" data-testid={`${id}-input`} />
        <button type="button" onClick={() => setShow(!show)} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted-ink)]" aria-label="إظهار كلمة المرور" data-testid={`${id}-toggle`}>
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
    </div>
  );
};

const useSubmit = () => {
  const [busy, setBusy] = useState(false);
  const run = async (e, fn) => {
    e.preventDefault();
    setBusy(true);
    try { await fn(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return [busy, run];
};

const safeRedirect = (r) => (r && r.startsWith("/") && !r.startsWith("//") ? r : "/account");

export function SignInPage() {
  usePageTitle("تسجيل الدخول");
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { signIn } = useMe();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, run] = useSubmit();
  const submit = (e) => run(e, async () => {
    const r = await publicApi.post("/auth/login", f);
    signIn(r.data.token, r.data.user);
    toast.success("تم تسجيل الدخول بنجاح");
    nav(safeRedirect(sp.get("redirect")), { replace: true });
  });
  return (
    <AuthShell title="تسجيل الدخول" subtitle="مرحباً بعودتك إلى أكاديمية هِمّة تم" testId="signin-page">
      <form onSubmit={submit} className="space-y-4" data-testid="signin-form">
        <Field id="signin-email" label="البريد الإلكتروني" type="email" dir="ltr" required autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <PasswordField id="signin-password" label="كلمة المرور" autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <div className="text-left"><Link to="/forgot-password" className="text-sm text-[var(--copper)]" data-testid="forgot-password-link">نسيت كلمة المرور؟</Link></div>
        <button disabled={busy} className="btn-copper w-full" data-testid="signin-submit-btn">{busy ? "جارٍ الدخول..." : "تسجيل الدخول"}</button>
        <p className="text-sm text-center text-[var(--muted-ink)]">ليس لديك حساب؟ <Link to={`/sign-up${sp.get("redirect") ? `?redirect=${encodeURIComponent(sp.get("redirect"))}` : ""}`} className="text-[var(--copper)] font-semibold" data-testid="goto-signup-link">إنشاء حساب</Link></p>
      </form>
    </AuthShell>
  );
}

export function SignUpPage() {
  usePageTitle("إنشاء حساب");
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { signIn } = useMe();
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });
  const [busy, run] = useSubmit();
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = (e) => run(e, async () => {
    if (f.password.length < MIN_PW) throw { response: { data: { detail: "كلمة المرور يجب أن تكون 6 خانات على الأقل" } } };
    if (f.password !== f.confirm) throw { response: { data: { detail: "كلمتا المرور غير متطابقتين" } } };
    const { confirm, ...body } = f;
    const r = await publicApi.post("/auth/register", body);
    signIn(r.data.token, r.data.user);
    toast.success("تم إنشاء حسابك بنجاح، أرسلنا رابط تأكيد إلى بريدك");
    nav(safeRedirect(sp.get("redirect")), { replace: true });
  });
  return (
    <AuthShell title="إنشاء حساب جديد" subtitle="انضم إلى أكاديمية هِمّة تم وابدأ رحلتك" testId="signup-page">
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <Field id="signup-name" label="الاسم الكامل" required minLength={2} value={f.name} onChange={set("name")} autoComplete="name" />
        <Field id="signup-email" label="البريد الإلكتروني" type="email" dir="ltr" required value={f.email} onChange={set("email")} autoComplete="email" />
        <Field id="signup-phone" label="رقم الهاتف (اختياري)" dir="ltr" inputMode="tel" placeholder="+968 9XXXXXXX" value={f.phone} onChange={set("phone")} />
        <PasswordField id="signup-password" label="كلمة المرور (6 خانات أو أكثر: أحرف أو أرقام أو رموز)" value={f.password} onChange={set("password")} />
        <PasswordField id="signup-confirm" label="تأكيد كلمة المرور" value={f.confirm} onChange={set("confirm")} />
        <button disabled={busy} className="btn-copper w-full" data-testid="signup-submit-btn">{busy ? "جارٍ الإنشاء..." : "إنشاء الحساب"}</button>
        <p className="text-sm text-center text-[var(--muted-ink)]">لديك حساب؟ <Link to="/sign-in" className="text-[var(--copper)] font-semibold" data-testid="goto-signin-link">تسجيل الدخول</Link></p>
      </form>
    </AuthShell>
  );
}

export function ForgotPasswordPage() {
  usePageTitle("استعادة كلمة المرور");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, run] = useSubmit();
  const submit = (e) => run(e, async () => {
    await publicApi.post("/auth/forgot-password", { email });
    setSent(true);
  });
  return (
    <AuthShell title="استعادة كلمة المرور" subtitle="أدخل بريدك وسنرسل لك رابطاً لتعيين كلمة مرور جديدة" testId="forgot-page">
      {sent ? (
        <p className="text-emerald-700 leading-loose" data-testid="forgot-sent-msg">إذا كان البريد مسجلاً لدينا فستصلك رسالة خلال دقائق تحتوي على رابط إعادة التعيين (صالح لمدة ساعة). تحقق من مجلد الرسائل غير المرغوبة أيضاً.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field id="forgot-email" label="البريد الإلكتروني" type="email" dir="ltr" required value={email} onChange={(e) => setEmail(e.target.value)} />
          <button disabled={busy} className="btn-copper w-full" data-testid="forgot-submit-btn">{busy ? "جارٍ الإرسال..." : "إرسال رابط الاستعادة"}</button>
        </form>
      )}
      <p className="text-sm text-center mt-5"><Link to="/sign-in" className="text-[var(--copper)]">العودة لتسجيل الدخول</Link></p>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  usePageTitle("تعيين كلمة مرور جديدة");
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { signIn } = useMe();
  const [f, setF] = useState({ password: "", confirm: "" });
  const [busy, run] = useSubmit();
  const submit = (e) => run(e, async () => {
    if (f.password !== f.confirm) throw { response: { data: { detail: "كلمتا المرور غير متطابقتين" } } };
    const r = await publicApi.post("/auth/reset-password", { token: sp.get("token") || "", password: f.password });
    signIn(r.data.token, r.data.user);
    toast.success("تم تعيين كلمة المرور الجديدة");
    nav("/account", { replace: true });
  });
  return (
    <AuthShell title="تعيين كلمة مرور جديدة" subtitle="6 خانات أو أكثر: أحرف أو أرقام أو رموز" testId="reset-page">
      <form onSubmit={submit} className="space-y-4">
        <PasswordField id="reset-password" label="كلمة المرور الجديدة" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <PasswordField id="reset-confirm" label="تأكيد كلمة المرور" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} />
        <button disabled={busy} className="btn-copper w-full" data-testid="reset-submit-btn">{busy ? "جارٍ الحفظ..." : "حفظ كلمة المرور"}</button>
      </form>
    </AuthShell>
  );
}

export function VerifyEmailPage() {
  usePageTitle("تأكيد البريد الإلكتروني");
  const [sp] = useSearchParams();
  const { reloadMe } = useMe();
  const [state, setState] = useState("loading");
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    publicApi.post("/auth/verify-email", { token: sp.get("token") || "" })
      .then(() => { setState("ok"); reloadMe(); })
      .catch(() => setState("error"));
  }, [sp, reloadMe]);
  return (
    <AuthShell title="تأكيد البريد الإلكتروني" testId="verify-email-page">
      <div className="text-center py-6">
        {state === "loading" && <Loader2 className="mx-auto animate-spin text-[var(--copper)]" size={48} />}
        {state === "ok" && <div data-testid="verify-email-success"><CheckCircle2 className="mx-auto text-emerald-600" size={56} /><p className="mt-4 font-semibold">تم تأكيد بريدك الإلكتروني بنجاح</p><Link to="/account" className="btn-copper mt-6">الذهاب إلى حسابي</Link></div>}
        {state === "error" && <div data-testid="verify-email-error"><XCircle className="mx-auto text-red-600" size={56} /><p className="mt-4 font-semibold">الرابط غير صالح أو منتهي الصلاحية</p><p className="text-sm text-[var(--muted-ink)] mt-2">يمكنك طلب رابط جديد من صفحة حسابك.</p></div>}
      </div>
    </AuthShell>
  );
}
